//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'
import * as moment from 'moment'

//#endregion

//#region Helper Imports

import { Dictionary, ToastHelper, LocalizationHelper, LogHelper, ConvertHelper, ConfigHelper } from '../../../../helpers'

//#endregion

//#region Service Imports

import { DataTypeService, DataTypeGroupService, DataTypeFormatter } from '../../../../services/data-type'
import { UserSessionService } from '../../../../services/user-session'

//#endregion

//#region Model Imports

import { OpImportData } from '../../../../models/util'
import { OpDataTypeInGroup, OpDataType } from '../../../../models/core'
import { Enums } from '../../../../models/common'

//#endregion

import { ImportService } from './import.service'


//#region Public Types

export const SERVICE_NAME: string = 'ImportGridDataService'

//#endregion


export class ImportDataService {

    //#region Ctor

    constructor(
        private dataTypeService: DataTypeService,
        private dataTypeGroupService: DataTypeGroupService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Data

    public prepareImportData(importData: OpImportData, headerDataTypeDict: Dictionary<OpDataType>, dataTypeFormatter: DataTypeFormatter): void {
        if (importData == null && importData.headers != null && headerDataTypeDict == null) {
            return
        }

        _.forEach(importData.headers, (header: string, headerIndex: number) => {
            const headerDataType: OpDataType = headerDataTypeDict.tryGetValue(header)

            if (headerDataType != null) {
                _.forEach(importData.data, (data: any[]) => {
                    if (headerDataType.idDataTypeClass === Enums.DataTypeClass.Datetime) {
                        data[headerIndex] = this.prepareDateValue(data[headerIndex], headerDataType, dataTypeFormatter)
                    }
                    else if (headerDataType.idDataTypeClass === Enums.DataTypeClass.Integer || headerDataType.idDataTypeClass === Enums.DataTypeClass.Real) {
                        data[headerIndex] = this.prepareNumberValue(data[headerIndex], headerDataType, dataTypeFormatter)
                    }
                })
            }
        })
    }

    private prepareDateValue(data: any, dataType: OpDataType, dataTypeFormatter: DataTypeFormatter): any {
        const format: string = dataTypeFormatter.getDateFormat(ConfigHelper.DateFormat.DATE_TIME_FORMAT_MOMENTJS, dataType.idDataType)
        const date: moment.Moment = moment(data, format)

        if (!date.isValid()) {
            return `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.InvalidDate)}. ${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CorrectFormat)}: ${format}.`
        }

        return data
    }

    private prepareNumberValue(data: any, dataType: OpDataType, dataTypeFormatter: DataTypeFormatter): any {
        if (dataType.idReferenceType == null) {
            const parsedNumber: number = ConvertHelper.localizedStringToNumber(data)

            return _.isFinite(parsedNumber) ? parsedNumber : null
        }

        return data
    }

    //#endregion


    //#region Headers

    /**
     * Returns a list of OpDataTypeInGroup from DataTypeGroup for the given module or created from import headers of the given OpImportData.
     */
    public getImportHeaderDataTypes(moduleName: Enums.SimaxModuleName, importData: OpImportData): JQueryPromise<OpDataTypeInGroup[]> {
        return this.dataTypeGroupService
            .getDataTypeInGroupByCategory(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__IMPORT_PARAMS, moduleName)
            .then((dataTypesInGroup: OpDataTypeInGroup[]) => {
                if (_.size(dataTypesInGroup) > 0) {
                    return _.clone(dataTypesInGroup)
                }

                return this
                    .createHeaderDataTypeDict(importData, dataTypesInGroup)
                    .then((headerDataTypeDict: Dictionary<OpDataType>) => {
                        return _.map(headerDataTypeDict.values(), (dataType: OpDataType) => this.dataTypeService.dataTypeToDataTypeInGroup(dataType))
                    })
            })
            .fail((error: any) => {
                ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(ImportService.LOG_PREFIX, `Unable to load import data type in group for module: ${moduleName}.`, error)

                return $.Deferred().reject(error).promise()
            })
    }

    /**
     * Creates a dictionary of header names and their corresponding OpDataType objects matched by the following alhorithm:
     * 1. DataTypeInGroup's toString value if any provided
     * 2. DataTypeInGroup's dataType descr toString
     * 3. DataTypeInGroup's dataType name value
     * 4. DataTypeInGroup's dataType idDataType value
     * 5. DataType's descr toString value
     * 6. DataType's name value
     * 7. DataType's idDataType value
     * 
     * All values are lowercased before comparison.
     */
    public createHeaderDataTypeDict(importData: OpImportData, dataTypesInGroup: OpDataTypeInGroup[] = null): JQueryPromise<Dictionary<OpDataType>> {
        return this
            .getDataTypes()
            .then((dataTypes: OpDataType[]) => {
                const headerDataTypesDict: Dictionary<OpDataType> = new Dictionary<OpDataType>(null)

                if (importData != null && importData.headers != null) {
                    _.forEach(importData.headers, (header: string) => {
                        const correspondingDataTypeInGroup: OpDataTypeInGroup = _.find(dataTypesInGroup, (dataTypeInGroup: OpDataTypeInGroup) => {
                            return this.isHeaderDataTypeInGroup(header, dataTypeInGroup)
                        })

                        if (correspondingDataTypeInGroup != null) {
                            headerDataTypesDict.addOrUpdate(header, correspondingDataTypeInGroup.dataType)
                        } else {
                            const correspondingDataType: OpDataType = _.find(dataTypes, (dataType: OpDataType) => {
                                return this.isHeaderDataType(header, dataType)
                            })

                            if (correspondingDataType != null) {
                                headerDataTypesDict.addOrUpdate(header, correspondingDataType)
                            }
                        }
                    })
                }

                return headerDataTypesDict
            })
    }

    private getDataTypes(): JQueryPromise<OpDataType[]> {
        return this.userSessionService
            .getDataProvider().DataType
            .getDataTypeDict()
            .then((dataTypeDict: Dictionary<OpDataType>) => {
                return dataTypeDict != null ? dataTypeDict.values() : []
            })
    }

    private isHeaderDataTypeInGroup(header: string, dataTypeInGroup: OpDataTypeInGroup): boolean {
        if (header == null || dataTypeInGroup == null) {
            return false
        }

        const headerLowerCase: string = header.toLocaleLowerCase()

        return dataTypeInGroup.toString().toLocaleLowerCase() === headerLowerCase || this.isHeaderDataType(header, dataTypeInGroup.dataType)
    }

    private isHeaderDataType(header: string, dataType: OpDataType): boolean {
        if (header == null || dataType == null) {
            return false
        }

        const headerLowerCase: string = header.toLocaleLowerCase()

        return dataType.descr != null && dataType.descr.toString().toLocaleLowerCase() === headerLowerCase ||
            dataType.name.toLocaleLowerCase() === headerLowerCase ||
            dataType.idDataType.toString() === headerLowerCase
    }

    //#endregion
}
